import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Clock } from 'lucide-react';
import { useT } from '../lib/i18n.js';
import { formatDate } from '../lib/properties.js';
import { readTrialNoticeAck, useSession, writeTrialNoticeAck } from '../stores/session.js';
import type { TrialStatus } from '../lib/api.js';

/**
 * Krok TRIAL1 (zadani bod 3) — PRUH „KONCI ZKUSEBNI OBDOBI".
 *
 * Ukazuje se podle SERVERU (`GET /api/me` -> `trial`), tedy podle tehoz
 * stavu, kterym se ridi brana zapisoveho API (402 po konci). Pruh proto
 * nemuze tvrdit neco jineho, nez co server pousti — stejny princip jako
 * `DemoBanner` a `flags.demo`.
 *
 * Kdy je videt (zadani: 14 dni, 3 dny a v den konce):
 *  * `daysLeft <= 14` ... „konci {datum}, zbyva N dni",
 *  * `daysLeft === 0` ... „konci dnes",
 *  * po konci bez platby ... „skoncilo, aplikace je jen ke cteni, data
 *    zustavaji" (DATA SE NEMAZOU, zadani bod 3).
 *
 * Kdo zaplatil (`paid`), pruh nevidi vubec — i kdyz datum konce zkusebniho
 * obdobi v DB zustava (historie, stejne jako sloupce `trial_*`).
 *
 * PRAH 14 je zde `TRIAL_BANNER_FROM_DAYS`, protoze je to TENTYZ prah, na
 * kterem posila prvni pripominku `sendTrialEndingNotices`
 * (`TRIAL_NOTICE_THRESHOLDS` v @reos/commerce). Administrace na server
 * kontext nema, takze cislo zije na dvou mistech; drzi je pohromade test
 * `trial-banner.spec.tsx` -> „prah pruhu = nejvzdalenejsi prah pripominek".
 */
export const TRIAL_BANNER_FROM_DAYS = 14;

/** Co ma pruh rict; `null` = nekreslit. */
export function trialBannerState(
  trial: TrialStatus | null,
): { readonly kind: 'before' | 'last' | 'expired'; readonly days: number } | null {
  if (trial === null || trial.endsAt === null) return null;
  // Zaplaceno = zkusebni obdobi uz neni tema, i kdyz datum v DB zustava.
  if (trial.paid) return null;
  if (trial.readOnly) return { kind: 'expired', days: 0 };
  const days = trial.daysLeft;
  if (days === null || days > TRIAL_BANNER_FROM_DAYS) return null;
  return days <= 0 ? { kind: 'last', days: 0 } : { kind: 'before', days };
}

/**
 * Krok PRO1 — UPOZORNENI 7 A 1 DEN PRED KONCEM V APLIKACI, KAZDE JEN JEDNOU.
 *
 * Pruh vyse je PRUBEZNY stav („zbyva N dni", od 14 dni). Upozorneni je neco
 * jineho: zprava, kterou makler VEZME NA VEDOMI a ktera se pak uz nevnucuje —
 * stejne jako e-mail, ktery prijde jednou.
 *
 * Prah NEPOCITA administrace: chodi hotovy ze serveru (`trial.noticeThreshold`
 * = `trialNoticeDue` v commerce), tedy TENTYZ, na ktery odesla e-mail. Kdyby
 * si ho pocitala sama, aplikace by upozornila na jiny den nez posta.
 *
 * `acked` je prah, ktery uzivatel odklikl. Prahy jdou od mirnejsiho
 * k urgentnejsimu (7 → 1), takze odkliknuti 7 NEZAVRE upozorneni na 1 den.
 */
export function trialNoticeState(
  trial: TrialStatus | null,
  acked: number | null,
): { readonly threshold: number; readonly last: boolean } | null {
  if (trial === null || trial.endsAt === null || trial.paid) return null;
  const threshold = trial.noticeThreshold ?? null;
  if (threshold === null || threshold === acked) return null;
  // „Konci dnes" mluvi jinak nez „zbyvaji dny" — tyz rozpad jako u e-mailu
  // (`trialNoticeMessageKeys` v commerce).
  return { threshold, last: (trial.daysLeft ?? threshold) <= 0 };
}

export function TrialBanner(): React.JSX.Element | null {
  const t = useT();
  const trial = useSession((s) => s.trial);
  const state = trialBannerState(trial);
  // Uloziste prohlizece smi jen `stores/session.ts` (krok S3) — tady jen
  // predvolba zobrazeni „tuhle zpravu uz jsem videl".
  const [acked, setAcked] = React.useState<number | null>(() =>
    readTrialNoticeAck(trial?.endsAt ?? null),
  );
  const notice = trialNoticeState(trial, acked);

  if (state === null || trial?.endsAt === null || trial === null) return null;
  const date = formatDate(trial.endsAt);

  if (notice !== null) {
    const endsAt = trial.endsAt;
    return (
      <section
        className="rh-a2-demobar"
        data-testid="trial-banner"
        data-trial-state={state.kind}
        data-trial-notice={notice.threshold}
        aria-labelledby="trial-banner-title"
      >
        <span className="rh-a2-demobar__row">
          <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
            <Clock size={14} strokeWidth={2.2} />
          </span>
          <span className="rh-a2-demobar__text">
            <strong id="trial-banner-title">
              {notice.last
                ? t('webAdmin.trial.banner.noticeLast', { date })
                : t('webAdmin.trial.banner.notice', { date, days: notice.threshold })}
            </strong>
          </span>
          <Link to="/nastaveni" className="rh-a2-demobar__toggle" data-testid="trial-banner-cta">
            {t('webAdmin.trial.banner.cta')}
          </Link>
          <button
            type="button"
            className="rh-a2-demobar__toggle"
            data-testid="trial-notice-ack"
            onClick={() => {
              setAcked(notice.threshold);
              writeTrialNoticeAck(endsAt, notice.threshold);
            }}
          >
            {t('webAdmin.trial.banner.noticeAck')}
          </button>
        </span>
      </section>
    );
  }

  const text =
    state.kind === 'before'
      ? t('webAdmin.trial.banner.before', { date, days: state.days })
      : state.kind === 'last'
        ? t('webAdmin.trial.banner.last', { date })
        : t('webAdmin.trial.banner.expired', { date });

  return (
    <section
      className="rh-a2-demobar"
      data-testid="trial-banner"
      data-trial-state={state.kind}
      aria-labelledby="trial-banner-title"
    >
      <span className="rh-a2-demobar__row">
        <span className="rh-a2-ico rh-a2-ico--sm" aria-hidden="true">
          <Clock size={14} strokeWidth={2.2} />
        </span>
        <span className="rh-a2-demobar__text">
          <strong id="trial-banner-title">{text}</strong>
        </span>
        <Link to="/nastaveni" className="rh-a2-demobar__toggle" data-testid="trial-banner-cta">
          {t('webAdmin.trial.banner.cta')}
        </Link>
      </span>
    </section>
  );
}
